import { ArrayUtils } from './ArrayUtils';

interface CallbackItem<T = never> {
	callback: (data: T | undefined) => void;
	useOnce?: boolean;
}

export type EventDispatcherListener<T = void> = Pick<EventDispatcher<T>, 'add' | 'remove' | 'addOnce'>;

export class EventDispatcher<T = never> {
	private _handlers: CallbackItem<T>[] = [];
	public get handlers(): CallbackItem<T>[] {
		return this._handlers;
	}

	public get numOfListeners() {
		return this._handlers.length;
	}

	public add(handler: (data?: T) => void) {
		if (!this._handlers.find(h => h.callback === handler)) {
			this._handlers.push({ callback: handler });
		}
	}

	public addOnce(handler: (data?: T) => void) {
		if (!this._handlers.find(h => h.callback === handler)) {
			this._handlers.push({ callback: handler, useOnce: true });
		}
	}

	public remove(handler: (data?: T) => void) {
		const item = this._handlers.find(h => h.callback === handler);
		if (!item) {
			return;
		}

		ArrayUtils.removeElement(this._handlers, item);
	}

	public trigger = (data?: T) => {
		for (let i = 0; i < this._handlers.length; i++) {
			const handler = this._handlers[i];
			try {
				handler.callback(data);
			} catch (e) {
				console.error('Triggering event, failed with error:');
				console.error(e);
			}

			if (handler.useOnce) {
				this._handlers.splice(i, 1);
				i--;
			}
		}
	};

	public clear() {
		this._handlers.length = 0;
	}
}
